import { useCallback, useEffect, useRef, useState } from 'react';
import { DocumentViewer } from './DocumentViewer';
import { TaskDrawer } from './TaskDrawer';
import { useAsk } from './Ask';
import { api, ApiError } from '../lib/api';
import type { Board, DocumentKind, ProjectDocument } from '../lib/types';
import { DOCUMENT_KIND_LABELS } from '../lib/types';
import { formatDate } from '../lib/dates';
import { DateCell } from './DateCell';

const KINDS: DocumentKind[] = ['change_request', 'handover', 'test_evidence', 'specification', 'other'];

/**
 * The paperwork against a project or a task: the signed change request, the handover, the
 * evidence of what was tested.
 *
 * `kind` and `signed` are their own controls rather than words in a title, because the
 * question people actually arrive with is "where is the signed CR" — and a list you have to
 * read to answer that is not an answer.
 */
export function DocumentList({
  scope,
  id,
  canUpload,
  compact,
}: {
  scope: 'projects' | 'work-items';
  id: string;
  canUpload: boolean;
  /** In the task drawer: no upload form until asked, and fewer columns. */
  compact?: boolean;
}) {
  const [documents, setDocuments] = useState<ProjectDocument[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ask = useAsk();
  const [adding, setAdding] = useState(false);
  const [find, setFind] = useState('');
  /** Which document is open on screen, if any. */
  const [viewing, setViewing] = useState<ProjectDocument | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState<DocumentKind>('change_request');
  const [signed, setSigned] = useState(false);
  const [signedOn, setSignedOn] = useState('');
  /** Which task the upload should be filed against, if any. Project uploads only. */
  const [onTask, setOnTask] = useState('');
  const picker = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  /** From the chooser or a drop — the same thing either way. */
  function choose(chosen: File | null) {
    setFile(chosen);
    // The filename is the obvious first guess at a title, and easy to correct.
    if (chosen && !title.trim()) setTitle(chosen.name.replace(/\.[^.]+$/, ''));
  }

  /** The project's tasks, for the picker — fetched when the form opens, not before. */
  const [tasks, setTasks] = useState<{ id: string; key: string; title: string }[]>([]);
  /** Separate from `tasks.length`: a project with no tasks has finished loading too. */
  const [tasksLoaded, setTasksLoaded] = useState(false);
  /** A task opened from a document's row, over the list rather than instead of it. */
  const [openTask, setOpenTask] = useState<{ id: string; projectId: string } | null>(null);
  /** Which row is being re-filed, if any — one at a time, in place. */
  const [moving, setMoving] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setDocuments(await api.get<ProjectDocument[]>(`/${scope}/${id}/documents`));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the documents');
      setDocuments([]);
    }
  }, [scope, id]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * The task list, only once the upload form is open.
   *
   * A project's board is a real payload, and nobody who is merely reading the documents should
   * pay for it. Closed work is included: evidence usually arrives after something is finished.
   */
  useEffect(() => {
    if (scope !== 'projects' || (!adding && moving === null) || tasksLoaded) return;
    let live = true;

    void api
      .get<Board>(`/projects/${id}/board?includeClosed=true`)
      .then((board) => {
        if (!live) return;
        const cards = board.columns.flatMap((column) => column.items);
        setTasks(
          cards
            .map((card) => ({ id: card.id, key: card.key, title: card.title }))
            .sort((a, b) => a.key.localeCompare(b.key, undefined, { numeric: true })),
        );
      })
      /* A picker that could not load is a missing convenience, not a failed upload. */
      .catch(() => undefined)
      .finally(() => {
        if (live) setTasksLoaded(true);
      });

    return () => {
      live = false;
    };
  }, [scope, adding, moving, id, tasksLoaded]);

  async function send() {
    if (!file || busy) return;
    setBusy(true);
    setError(null);
    try {
      /**
       * Multipart, so the browser sets the boundary — and no Content-Type of our own, which
       * would replace it and leave the server unable to find the parts.
       */
      const form = new FormData();
      form.append('file', file);
      form.append('kind', kind);
      if (title.trim()) form.append('title', title.trim());
      if (signed) form.append('signed', 'true');
      if (signed && signedOn) form.append('signedOn', signedOn);
      // Sent only when chosen: an empty field would read as "take it off the task".
      if (scope === 'projects' && onTask) form.append('workItemId', onTask);

      await api.upload(`/${scope}/${id}/documents`, form);

      setFile(null);
      setTitle('');
      setSigned(false);
      setSignedOn('');
      setOnTask('');
      setAdding(false);
      if (picker.current) picker.current.value = '';
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not upload that file');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Filing a document that is already here against a task, or taking it off one.
   *
   * Nothing moves on disk and nothing is copied — the row simply says which task it belongs
   * to. Which is the point: a file put in the wrong place should not have to be deleted and
   * uploaded again, least of all a signed one.
   */
  async function moveTo(document: ProjectDocument, workItemId: string) {
    setBusy(true);
    setError(null);
    try {
      // Empty means the project at large. The API reads that as an instruction, not a blank.
      await api.patch(`/documents/${document.id}`, { workItemId });
      setMoving(null);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not move that document');
    } finally {
      setBusy(false);
    }
  }

  async function markSigned(document: ProjectDocument) {
    setBusy(true);
    try {
      await api.patch(`/documents/${document.id}`, { signed: true, signedOn: today() });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not update that document');
    } finally {
      setBusy(false);
    }
  }

  async function remove(document: ProjectDocument) {
    const sure = await ask.confirm({
      title: `Delete “${document.title}”?`,
      body: 'The file goes with it. If it is a signed change request or handover evidence, keep it.',
      confirmLabel: 'Delete it',
      danger: true,
    });
    if (!sure) return;
    setBusy(true);
    try {
      await api.del(`/documents/${document.id}`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete that document');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Matches what somebody would remember about a document: what it is called, the filename it
   * arrived with, what kind it is, and who sent it. A project that has run for a year has
   * dozens of these and "CR" is the only thing anybody recalls.
   */
  const all = documents ?? [];
  const query = find.trim().toLowerCase();
  const shown = query
    ? all.filter((document) =>
        [
          document.title,
          document.originalName,
          DOCUMENT_KIND_LABELS[document.kind],
          document.uploadedBy?.name ?? '',
          /* "Where is the thing on ABC-14" is how people actually ask. */
          document.workItem ? `${document.workItem.key} ${document.workItem.title}` : '',
          document.signed ? 'signed' : '',
        ]
          .join('\n')
          .toLowerCase()
          .includes(query),
      )
    : all;

  /** Only worth the space once there are enough to lose one in. */
  const searchable = all.length > 4;

  return (
    <div className={compact ? 'card mt-2' : 'card'}>
      <div className="card-head">
        <h3>Documents</h3>
        <span className="row" style={{ gap: 10 }}>
          {searchable && (
            <input
              type="search"
              className="doc-find"
              value={find}
              placeholder="Find a document"
              onChange={(event) => setFind(event.target.value)}
            />
          )}
          {all.length > 0 && (
            <span className="dim" style={{ fontSize: 12 }}>
              {query ? `${shown.length} of ${all.length}` : all.length}
            </span>
          )}
          {/*
            Says what it does, and looks like the thing to press.
            It was "+ Add" in the quiet style, sitting beside a search box and a count — on a
            task drawer that reads as chrome rather than as the way to attach a file, and it
            never said what it would add.
          */}
          {canUpload && !adding && (
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setAdding(true)}>
              {scope === 'work-items' ? 'Attach a file' : 'Upload a document'}
            </button>
          )}
        </span>
      </div>

      <div className="card-pad">
        {error && <div className="callout mb-2">{error}</div>}

        {documents === null ? (
          <span className="dim">Loading…</span>
        ) : all.length === 0 ? (
          <div>
            <p className="dim" style={{ margin: 0, fontSize: 13 }}>
              {scope === 'work-items'
                ? 'Nothing attached yet. Screenshots, exports and the evidence of what was tested belong here.'
                : 'Nothing filed yet. Signed change requests, handover notes and test evidence belong here.'}
            </p>
            {/*
              The empty state is exactly where somebody is looking for the way in, so it
              carries the control too rather than sending them back up to the header.
            */}
            {canUpload && !adding && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                style={{ marginTop: 10 }}
                onClick={() => setAdding(true)}
              >
                {scope === 'work-items' ? 'Attach the first file' : 'Upload the first document'}
              </button>
            )}
          </div>
        ) : shown.length === 0 ? (
          <p className="dim" style={{ margin: 0, fontSize: 13 }}>
            None of the {all.length} documents here match “{find.trim()}”.
          </p>
        ) : (
          <div className="stack-sm">
            {shown.map((document) => (
              <div key={document.id} className="doc-row">
                <span className="doc-kind">{DOCUMENT_KIND_LABELS[document.kind]}</span>

                <span className="grow" style={{ minWidth: 0 }}>
                  <span className="row" style={{ gap: 6 }}>
                    {/**
                     * The title opens what it can and downloads what it cannot.
                     *
                     * `viewable` comes from the API, so this never offers a reader for
                     * something nothing can show — an Outlook `.msg`, say, which stays a
                     * plain link for the browser's own download to handle.
                     */}
                    {document.viewable ? (
                      <button
                        type="button"
                        className="row-title"
                        style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', cursor: 'pointer' }}
                        title="Open it here"
                        onClick={() => setViewing(document)}
                      >
                        {document.title}
                      </button>
                    ) : (
                      <a href={`/api/documents/${document.id}/download`} className="row-title">
                        {document.title}
                      </a>
                    )}
                    {document.signed && <span className="badge badge-ok">signed</span>}
                    {/*
                      Which task it belongs to, on the project's list only — on the task's own
                      list every row would say the same thing.

                      It OPENS the task over this list rather than navigating to it: losing
                      your place in the documents to glance at a task is the thing that makes
                      people not bother.
                    */}
                    {scope === 'projects' && document.workItem && (
                      <button
                        type="button"
                        className="chip-toggle doc-task"
                        title={`On ${document.workItem.key} — ${document.workItem.title}`}
                        onClick={() => setOpenTask({ id: document.workItem!.id, projectId: document.projectId })}
                      >
                        {document.workItem.key}
                      </button>
                    )}
                  </span>
                  <span className="doc-meta">
                    {document.originalName} · {readableSize(document.sizeBytes)}
                    {document.uploadedBy ? ` · ${document.uploadedBy.name}` : ''}
                    {document.signedOn ? ` · signed ${formatDate(document.signedOn)}` : ''}
                  </span>

                  {moving === document.id && (
                    <span className="row wrap" style={{ gap: 6, marginTop: 6 }}>
                      {/*
                        Not rendered until the tasks are in.

                        An empty select would start on "The project" while the document is
                        sitting on a task — the control would be showing the wrong answer to
                        the question it is asking.
                      */}
                      {!tasksLoaded ? (
                        <span className="dim" style={{ fontSize: 12 }}>Loading this project’s tasks…</span>
                      ) : tasks.length === 0 ? (
                        <span className="dim" style={{ fontSize: 12 }}>
                          There are no tasks on this project yet.
                        </span>
                      ) : (
                        <select
                          className="input-sm"
                          style={{ maxWidth: 340 }}
                          disabled={busy}
                          defaultValue={document.workItem?.id ?? ''}
                          onChange={(event) => void moveTo(document, event.target.value)}
                        >
                          <option value="">The project — not a particular task</option>
                          {tasks.map((task) => (
                            <option key={task.id} value={task.id}>
                              {task.key} — {task.title}
                            </option>
                          ))}
                        </select>
                      )}
                      <button type="button" className="btn btn-quiet btn-sm" onClick={() => setMoving(null)}>
                        Cancel
                      </button>
                    </span>
                  )}
                </span>

                {document.viewable && (
                  <button
                    type="button"
                    className="btn btn-quiet btn-sm"
                    onClick={() => setViewing(document)}
                  >
                    Open
                  </button>
                )}
                {/*
                  Only on the project's list, where every document on the project is in front
                  of you and moving one between tasks makes sense. Says which way it goes:
                  a document already on a task is being moved, one that is not is being filed.
                */}
                {canUpload && scope === 'projects' && (
                  <button
                    type="button"
                    className="btn btn-quiet btn-sm"
                    title={
                      document.workItem
                        ? 'Move it to another task, or back to the project'
                        : 'File it against one of this project’s tasks'
                    }
                    onClick={() => setMoving(moving === document.id ? null : document.id)}
                  >
                    {document.workItem ? 'Move' : 'File on a task'}
                  </button>
                )}
                {/*
                  Kept for every row, viewable or not: reading it on screen and keeping a copy
                  are different things, and somebody who wants the file still wants the file.
                */}
                <a
                  href={`/api/documents/${document.id}/download`}
                  className="btn btn-quiet btn-sm"
                  title="Save a copy"
                >
                  Download
                </a>
                {canUpload && !document.signed && document.kind === 'change_request' && (
                  <button
                    type="button"
                    className="btn btn-quiet btn-sm"
                    disabled={busy}
                    title="Mark this change request as signed"
                    onClick={() => void markSigned(document)}
                  >
                    Mark signed
                  </button>
                )}
                {document.canDelete && (
                  <button
                    type="button"
                    className="btn btn-quiet btn-sm"
                    disabled={busy}
                    onClick={() => void remove(document)}
                  >
                    Delete
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {canUpload && adding && (
          <div className="stack-sm" style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--line)' }}>
            {/*
              A box that says what to do, rather than the browser's own "Choose File — no file
              chosen", which on a task drawer read as small grey chrome and not as the place a
              file goes. The real input is inside the label, so clicking anywhere on the box
              opens the chooser; a file can also be dropped on it.
            */}
            <label
              className={`file-drop${dragging ? ' file-drop-over' : ''}${file ? ' file-drop-chosen' : ''}`}
              onDragOver={(event) => {
                event.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(event) => {
                event.preventDefault();
                setDragging(false);
                choose(event.dataTransfer.files?.[0] ?? null);
              }}
            >
              <input
                ref={picker}
                type="file"
                className="file-drop-input"
                onChange={(event) => choose(event.target.files?.[0] ?? null)}
              />
              {file ? (
                <>
                  <span className="file-drop-name">{file.name}</span>
                  <span className="dim">{readableSize(file.size)}</span>
                  <span className="btn btn-quiet btn-sm file-drop-button">Choose a different file</span>
                </>
              ) : (
                <>
                  <span className="btn btn-primary btn-sm file-drop-button">Choose a file</span>
                  <span className="dim">or drag it here</span>
                </>
              )}
            </label>

            <div className="row wrap" style={{ gap: 8 }}>
              <label className="field" style={{ marginBottom: 0, minWidth: 180 }}>
                <span className="field-label">What is it?</span>
                <select value={kind} onChange={(event) => setKind(event.target.value as DocumentKind)}>
                  {KINDS.map((option) => (
                    <option key={option} value={option}>
                      {DOCUMENT_KIND_LABELS[option]}
                    </option>
                  ))}
                </select>
              </label>

              <label className="field grow" style={{ marginBottom: 0, minWidth: 200 }}>
                <span className="field-label">Call it</span>
                <input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="CR023 signed" />
              </label>
            </div>

            {/*
              Filing it against a task, from here.

              The same file either way — a document on a task shows in this list too — so this
              is not a copy, it is where it belongs. Optional, because most of what is filed on
              a project belongs to the project.
            */}
            {scope === 'projects' && tasksLoaded && tasks.length > 0 && (
              <label className="field" style={{ marginBottom: 0, maxWidth: 420 }}>
                <span className="field-label">Attach it to a task (optional)</span>
                <select value={onTask} onChange={(event) => setOnTask(event.target.value)}>
                  <option value="">The project — not a particular task</option>
                  {tasks.map((task) => (
                    <option key={task.id} value={task.id}>
                      {task.key} — {task.title}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <label className="row" style={{ gap: 7, fontSize: 13 }}>
              <input type="checkbox" checked={signed} onChange={(event) => setSigned(event.target.checked)} />
              This one is signed
            </label>

            {signed && (
              <div className="field" style={{ marginBottom: 0, maxWidth: 200 }}>
                <span className="field-label">Signed on</span>
                <DateCell
                  value={signedOn || null}
                  editable
                  emptyLabel="Pick a date"
                  max={today()}
                  onChange={(next) => setSignedOn(next ?? '')}
                />
              </div>
            )}

            <div className="row" style={{ gap: 7 }}>
              <button type="button" className="btn btn-primary btn-sm" disabled={!file || busy} onClick={send}>
                {busy ? 'Uploading…' : 'Upload'}
              </button>
              <button
                type="button"
                className="btn btn-quiet btn-sm"
                onClick={() => {
                  setAdding(false);
                  setFile(null);
                  setOnTask('');
                  setError(null);
                }}
              >
                Cancel
              </button>
              <span className="dim" style={{ fontSize: 11 }}>
                PDF, Word, Excel, images or text. Up to 25MB.
              </span>
            </div>
          </div>
        )}
      </div>

      {viewing && <DocumentViewer document={viewing} onClose={() => setViewing(null)} />}

      {/* Edits made in there can add or remove files, so the list behind refreshes on close. */}
      {openTask && (
        <TaskDrawer
          itemId={openTask.id}
          projectId={openTask.projectId}
          onClose={() => setOpenTask(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

const today = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);

/** Bytes as somebody would say them. */
function readableSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
